import { Component, TemplateRef, ViewChild, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, catchError, first } from 'rxjs';
import { Constants } from 'src/app/core/constants';
import { Languages } from 'src/app/core/enums/languages.enum';
import { ErrorModel } from 'src/app/core/models/error-model.interface';
import { LoginUserFormModel } from 'src/app/core/models/user/login-user-form.model';
import { AuthService } from 'src/app/core/services/auth.service';
import { ToastService } from 'src/app/core/services/toast.service';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    providers: [LoginUserFormModel],
    standalone: false
})
export class LoginComponent {
  formModel = inject(LoginUserFormModel);
  private authService = inject(AuthService);
  private toastService = inject(ToastService);
  private translateService = inject(TranslateService);

  @ViewChild('dangerTpl')
  public dangerTpl!: TemplateRef<unknown>;

  // These fields just for ease of use on template
  public emailFormControl: FormControl<string>;
  public passwordFormControl: FormControl<string>;

  public callbackError?: string;

  constructor() {
    const formModel = this.formModel;

    this.emailFormControl = formModel.form.controls.email;
    this.passwordFormControl = formModel.form.controls.password;

    const language =
      window.localStorage.getItem('interfaceLanguage') ??
      Constants.DefaultLanguage;

    this.formModel.form.controls.interfaceLanguage.setValue(
      Languages[language as keyof typeof Languages],
    );
  }

  public submitForm(): void {
    const user = this.formModel.form.getRawValue();

    this.authService
      .signIn(user)
      .pipe(
        first(),
        catchError((error) => {
          this.callbackError = this.resolveLoginErrorMessage(error);
          this.toastService.show({
            template: this.dangerTpl,
            className: 'toast-danger',
          });

          return EMPTY;
        }),
      )
      .subscribe();
  }

  private resolveLoginErrorMessage(error: HttpErrorResponse): string {
    const errorModel = error.error as ErrorModel | null;
    const rawKey = errorModel?.i18nKey?.trim();
    const normalizedKey = rawKey
      ? rawKey
          .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
          .replace(/[\s-]+/g, '_')
          .toUpperCase()
      : 'UNKNOWN_ERROR';

    return this.translateService.instant(`ERRORS.${normalizedKey}`);
  }
}
